import React from 'react';
import { Navigate, RouteObject, useParams } from 'react-router-dom';
import loadable from '@loadable/component';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { UserModel } from '@/models/UserModel';

import { UserLoader } from '../middleware/UserLoader';
import { ProtectedRoute } from '../ProtectedRoute';
import { RoutePath } from '../RoutePath';

const StaffFacesheet = loadable(
  () => import('@/pages/AdminHub/Staff/StaffFacesheet')
); // prettier-ignore
const StaffSystemPermissions = loadable(
  () => import('@/pages/AdminHub/Staff/StaffSystemPermissions')
); // prettier-ignore
const StaffPage = loadable(() => import('@/pages/AdminHub/Staff'));
const StaffAccess = loadable(
  () => import('@/pages/AdminHub/Staff/StaffAccess')
); // prettier-ignore
const StaffAccessResidents = loadable(
  () => import('@/pages/AdminHub/Staff/StaffAccess/StaffAccessResidents')
); // prettier-ignore
const StaffAccessCommunity = loadable(
  () => import('@/pages/AdminHub/Staff/StaffAccess/StaffAccessCommunity')
); // prettier-ignore
const StaffNotificationPreferences = loadable(
  () => import('@/pages/AdminHub/Staff/Notifications')
); // prettier-ignore

export enum StaffRoutes {
  Staff = '/:domain/admin-hub/staff',
  StaffFacesheet = '/:domain/admin-hub/staff/:staff_id/facesheet',
  StaffAccess = '/:domain/admin-hub/staff/:staff_id/access',
  StaffAccessResidents = '/:domain/admin-hub/staff/:staff_id/access/residents',
  StaffAccessCommunity = '/:domain/admin-hub/staff/:staff_id/access/community',
  StaffSystemPermissions = '/:domain/admin-hub/staff/:staff_id/system-permissions',
  StaffUserSettingsPage = '/:domain/admin-hub/staff/:staff_id/user-settings',
  StaffNotificationPreferences = '/:domain/admin-hub/staff/:staff_id/notification-preferences'
}

interface RawStaffPageGuardProps {
  user: UserModel;
  staffId: string | undefined;
}

export const RawStaffPageGuard: React.FC<RawStaffPageGuardProps> = ({
  user,
  staffId
}) => {
  if (!user.hasMinimumUserRole('L1') && String(user.id) !== staffId) {
    return <Navigate to={RoutePath.Profile} replace />;
  }
  return <StaffPage />;
};

const StaffPageGuard = () => {
  const { staff_id } = useParams();
  const user = useCurrentUser().data;
  if (!user) {
   return null;
}
  return <RawStaffPageGuard user={user} staffId={staff_id} />;
};

export const getStaffRoutes = (): RouteObject[] => {
  return [
    {
      path: StaffRoutes.Staff,
      element: (
        <UserLoader>
          <StaffPageGuard />
        </UserLoader>
      ),
      children: [
        {
          path: StaffRoutes.StaffFacesheet,
          element: <ProtectedRoute role="L1" component={StaffFacesheet} />
        },
        {
          path: StaffRoutes.StaffAccess,
          element: <ProtectedRoute role="L1" component={StaffAccess} />,
          children: [
            {
              path: StaffRoutes.StaffAccessCommunity,
              element: (
                <ProtectedRoute role="L1" component={StaffAccessCommunity} />
              )
            },
            {
              path: StaffRoutes.StaffAccessResidents,
              element: (
                <ProtectedRoute role="L1" component={StaffAccessResidents} />
              )
            }
          ]
        },
        {
          path: StaffRoutes.StaffSystemPermissions,
          element: (
            <ProtectedRoute role="L1" component={StaffSystemPermissions} />
          )
        },
        {
          path: StaffRoutes.StaffNotificationPreferences,
          element: <StaffNotificationPreferences />
        }
      ]
    }
  ];
};
